import * as React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Avatar,
  Badge,
  Box,
  Center,
  Divider,
  Flex,
  HStack,
  Icon,
  Image,
  Link,
  Spinner,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import { StarIcon } from "@chakra-ui/icons";
import { LogoIcon } from "../../icons/LogoIcon";
import { TrackingMap } from "../../components/Tracking/TrackingMap";
import { getCarImage } from "../../utils/carImage";
import {
  TrackingStatus,
  TrackTripError,
  TrackTripErrorReason,
  useTrackTripService,
} from "../../services/trackTripService";
import {
  getStatusMeta,
  getStatusColor,
  getStatusTextColor,
} from "../../utils/tripStatus";
import { storeLinks } from "../../utils/constants";

const Header = () => (
  <HStack
    as="header"
    bgColor="primary.1000"
    h="64px"
    px={{ base: "20px", md: "10%" }}
    borderBottom="0.5px solid"
    borderColor="primary.800"
    alignItems="center"
    flexShrink={0}
  >
    <Link href="/" aria-label="ViajáYa">
      <LogoIcon h={20} w={20} />
    </Link>
  </HStack>
);

const FullScreen: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Flex direction="column" h="100vh" bg="primary.1000">
    <Header />
    {children}
  </Flex>
);

const CenteredMessage: React.FC<{ title: string; description: string }> = ({
  title,
  description,
}) => (
  <Center flex={1} px={6}>
    <VStack spacing={3} maxW="360px" textAlign="center">
      <Text color="white" fontSize="xl" fontWeight="bold">
        {title}
      </Text>
      <Text color="muted.400" fontSize="sm">
        {description}
      </Text>
      <Link
        href={storeLinks.wsp}
        isExternal
        color="info.main"
        fontSize="sm"
        fontWeight="semibold"
        pt={2}
      >
        Contactar soporte
      </Link>
    </VStack>
  </Center>
);

const InfoRow: React.FC<{ label: string; value?: string }> = ({
  label,
  value,
}) => {
  if (!value) return null;
  return (
    <HStack justify="space-between" w="full">
      <Text color="muted.400" fontSize="sm">
        {label}
      </Text>
      <Text color="white" fontSize="sm" fontWeight="semibold">
        {value}
      </Text>
    </HStack>
  );
};

const ColorRow: React.FC<{ color?: string }> = ({ color }) => {
  if (!color) return null;
  const carImage = getCarImage(color);
  return (
    <HStack justify="space-between" w="full">
      <Text color="muted.400" fontSize="sm">
        Color
      </Text>
      {carImage ? (
        <Image
          src={carImage}
          alt="Vehículo"
          h="40px"
          w="auto"
          objectFit="contain"
        />
      ) : (
        <Box
          w="18px"
          h="18px"
          borderRadius="full"
          bg={color}
          border="1px solid"
          borderColor="whiteAlpha.500"
        />
      )}
    </HStack>
  );
};

const InfoPanel: React.FC<{ data: TrackingStatus }> = ({ data }) => {
  const { status } = data;
  // const status = "arrived";
  const meta = getStatusMeta(status);
  const statusColor = getStatusColor(status);
  const statusTextColor = getStatusTextColor(status);
  const { driver, vehicle, etaMinutes } = data;

  const vehicleParts = [vehicle.brand, vehicle.model].filter(Boolean);
  const vehicleName = vehicleParts.join(" ");

  return (
    <Stack
      spacing={5}
      p={{ base: 5, md: 8 }}
      w={{ base: "full", md: "420px" }}
      maxW="full"
      bg="primary.1000"
      overflowY="auto"
    >
      <Box>
        <Flex alignItems="center" justifyContent="space-between">
          <Badge
            bg={statusColor}
            color={statusTextColor}
            px={3}
            py={1}
            borderRadius="full"
            fontSize="xs"
            textTransform="uppercase"
            letterSpacing="0.5px"
          >
            {meta.label}
          </Badge>
          {typeof etaMinutes === "number" && !meta.terminal && (
            <HStack align="baseline">
              <Text color="white" fontSize="sm" fontWeight="bold">
                {etaMinutes}
              </Text>
              <Text color="muted.400" fontSize="xs">
                min
              </Text>
            </HStack>
          )}
        </Flex>
        <Text color="muted.300" fontSize="sm" mt={3}>
          {meta.description}
        </Text>
      </Box>

      {(driver.name || driver.photoUrl) && (
        <>
          <Divider borderColor="primary.800" />
          <HStack spacing={4}>
            <Avatar
              size="md"
              name={driver.name}
              src={driver.photoUrl}
              bg="primary.700"
            />
            <VStack align="start" spacing={0}>
              <Text color="white" fontWeight="semibold">
                {driver.name ?? "Tu conductor"}
              </Text>
              {driver.rating && (
                <HStack spacing={1}>
                  <Icon as={StarIcon} color="warning.main" boxSize={3} />
                  <Text color="muted.400" fontSize="sm">
                    {driver.rating}
                  </Text>
                </HStack>
              )}
            </VStack>
          </HStack>
        </>
      )}

      {(vehicleName || vehicle.patent) && (
        <>
          <Divider borderColor="primary.800" />
          <Stack spacing={3}>
            <Text color="muted.400" fontSize="xs" textTransform="uppercase">
              Vehículo
            </Text>
            <ColorRow color={vehicle.color} />
            <InfoRow label="Patente" value={vehicle.patent} />
            <InfoRow label="Modelo" value={vehicleName || undefined} />
          </Stack>
        </>
      )}
    </Stack>
  );
};

const TrackTrip = () => {
  const { token } = useParams<{ token: string }>();
  const { trackTrip } = useTrackTripService();

  const [data, setData] = useState<TrackingStatus | undefined>(undefined);
  const [errorReason, setErrorReason] = useState<TrackTripErrorReason | null>(
    null,
  );
  const [loading, setLoading] = useState(true);

  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const isMounted = useRef(true);

  const poll = useCallback(async () => {
    if (!token) {
      setErrorReason("not-found");
      setLoading(false);
      return;
    }
    try {
      const result = await trackTrip(token);
      if (!isMounted.current) return;
      setData(result);
      setErrorReason(null);
      setLoading(false);

      // Reprogramar el polling solo si el viaje no está en estado terminal.
      if (result.pollingIntervalMs && result.pollingIntervalMs > 0) {
        timeoutRef.current = setTimeout(poll, result.pollingIntervalMs);
      }
    } catch (error) {
      if (!isMounted.current) return;
      setLoading(false);
      setErrorReason(
        error instanceof TrackTripError ? error.reason : "unknown",
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    isMounted.current = true;
    poll();
    return () => {
      isMounted.current = false;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [poll]);

  if (loading) {
    return (
      <FullScreen>
        <Center flex={1}>
          <Spinner color="info.main" size="lg" />
        </Center>
      </FullScreen>
    );
  }

  if (errorReason === "not-found") {
    return (
      <FullScreen>
        <CenteredMessage
          title="Viaje no encontrado"
          description="El link de seguimiento no es válido o ya no está disponible."
        />
      </FullScreen>
    );
  }

  if (errorReason === "expired") {
    return (
      <FullScreen>
        <CenteredMessage
          title="Seguimiento finalizado"
          description="El seguimiento de este viaje expiró. Si necesitás ayuda, contactá a soporte."
        />
      </FullScreen>
    );
  }

  if (errorReason || !data) {
    return (
      <FullScreen>
        <CenteredMessage
          title="No pudimos cargar el viaje"
          description="Ocurrió un error al obtener el estado del viaje. Volvé a intentar más tarde."
        />
      </FullScreen>
    );
  }

  return (
    <FullScreen>
      <Flex flex={1} direction={{ base: "column", md: "row" }} minH={0}>
        <Box flex={1} minH={{ base: "45vh", md: "auto" }}>
          <TrackingMap
            driverLat={data.vehicle.lat}
            driverLng={data.vehicle.lng}
            polyline={data.polyline}
            polylineStarted={data.polylineStarted}
            vehicleColor={data.vehicle.color}
            rideStarted={
              data.status === "carrying" ||
              data.status === "arrived" ||
              data.status === "finished"
            }
          />
        </Box>
        <InfoPanel data={data} />
      </Flex>
    </FullScreen>
  );
};

export default TrackTrip;
